import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  House, ChatCircleDots, Briefcase, CalendarBlank, UsersThree, Folder,
  ListChecks, UsersFour, Storefront, ChartLine, BookmarkSimple, Gear,
  MagnifyingGlass, Bell, Sparkle, List, X, ArrowUp, SignOut,
  User, ChatCircle,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import api, { API } from "../lib/api";
import TriforceCreate from "./TriforceCreate";
import HSTClock from "./HSTClock";

/**
 * CommandShell — the persistent chrome around every Command Center
 * screen. Left rail (12 modules), top bar (search + notifications + AI
 * button + avatar), plus a mobile drawer variant that collapses under
 * ~1024 px. Skinned in the existing black-marble + gold luxury palette.
 */

// Single source of truth for the module list. Adding a route here plus
// a `<Route/>` in App.js is all that's needed to plug a new module in.
const NAV = [
  { key: "home",       to: "/app",             label: "Home",       icon: House },
  { key: "messages",   to: "/app/messages",    label: "Messages",   icon: ChatCircleDots },
  { key: "jobs",       to: "/app/jobs",        label: "Jobs",       icon: Briefcase },
  { key: "events",     to: "/app/events",      label: "Events",     icon: CalendarBlank },
  { key: "meetups",    to: "/app/meetups",     label: "Meetups",    icon: UsersThree },
  { key: "projects",   to: "/app/projects",    label: "Projects",   icon: Folder },
  { key: "tasks",      to: "/app/tasks",       label: "Tasks",      icon: ListChecks },
  { key: "teams",      to: "/app/teams",       label: "Teams",      icon: UsersFour },
  { key: "businesses", to: "/app/businesses",  label: "Businesses", icon: Storefront },
  { key: "analytics",  to: "/app/analytics",   label: "Analytics",  icon: ChartLine },
  { key: "saved",      to: "/app/saved",       label: "Saved",      icon: BookmarkSimple },
  { key: "settings",   to: "/app/settings",    label: "Settings",   icon: Gear },
];

export default function CommandShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [counts, setCounts] = useState({ messages_unread: 0, friend_requests: 0, total: 0 });

  // Close mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Poll notification counts for sidebar + top-bar badges.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const { data } = await api.get("/notifications/counts");
        if (!cancelled) setCounts(data);
      } catch { /* ignore transient errors */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.user_id]);

  // Global ⌘K / Ctrl+K shortcut opens the search palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen text-[#F2DC8C]"
         style={{
           background:
             "radial-gradient(circle at 20% -10%, rgba(212,175,55,0.10) 0%, transparent 40%)," +
             "radial-gradient(circle at 90% 110%, rgba(255,215,0,0.06) 0%, transparent 45%)," +
             "linear-gradient(180deg, #0a0703 0%, #050300 100%)",
         }}
         data-testid="command-shell">
      {/* ─── Left sidebar (desktop only) ─────────────────────────── */}
      <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-[260px] z-40 flex-col"
             data-testid="command-sidebar"
             style={{
               background: "linear-gradient(180deg, rgba(20,14,4,0.95) 0%, rgba(5,3,0,0.98) 100%)",
               borderRight: "1px solid rgba(212,175,55,0.35)",
               backdropFilter: "blur(20px)",
             }}>
        <SidebarInner user={user} pathname={location.pathname} onLogout={logout} counts={counts}/>
      </aside>

      {/* ─── Mobile drawer ───────────────────────────────────────── */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
              onClick={() => setDrawerOpen(false)}
              data-testid="command-drawer-scrim"
            />
            <motion.aside
              key="drawer"
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "spring", stiffness: 260, damping: 32 }}
              className="fixed top-0 left-0 bottom-0 w-[260px] z-50 flex flex-col lg:hidden"
              data-testid="command-drawer"
              style={{
                background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
                borderRight: "1px solid rgba(212,175,55,0.55)",
              }}>
              <SidebarInner user={user} pathname={location.pathname} onLogout={logout} counts={counts}
                            onClose={() => setDrawerOpen(false)}/>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ─── Content column (offset for sidebar on desktop) ──────── */}
      <div className="lg:pl-[260px]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-[64px] flex items-center gap-3 px-4 sm:px-6"
                data-testid="command-topbar"
                style={{
                  background: "rgba(5,3,0,0.75)",
                  backdropFilter: "blur(18px)",
                  borderBottom: "1px solid rgba(212,175,55,0.35)",
                }}>
          {/* Hamburger — mobile only */}
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="command-menu-toggle"
            className="lg:hidden w-10 h-10 rounded-xl flex items-center justify-center transition-transform active:scale-95"
            style={{ border: "1px solid rgba(212,175,55,0.5)", background: "rgba(0,0,0,0.4)" }}
            aria-label="Open navigation">
            <List size={18} className="text-[#FFD700]"/>
          </button>

          {/* Global search */}
          <div className="flex-1 max-w-2xl">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="command-search-open"
              className="w-full h-11 flex items-center gap-3 px-4 rounded-2xl text-[13px] text-[#F2DC8C]/70 hover:text-[#FFD700] transition-colors"
              style={{
                background: "rgba(0,0,0,0.5)",
                border: "1px solid rgba(212,175,55,0.4)",
                boxShadow: "inset 0 0 22px rgba(212,175,55,0.05)",
              }}>
              <MagnifyingGlass size={15} weight="bold" className="text-[#FFD700]"/>
              <span className="truncate">Search anything…</span>
              <span className="hidden sm:inline-block ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md"
                    style={{ border: "1px solid rgba(212,175,55,0.45)", color: "#F2DC8C" }}>⌘K</span>
            </button>
          </div>

          {/* Right cluster */}
          <div className="flex items-center gap-2 ml-auto">
            <TopIcon icon={Sparkle} testid="command-ai" title="Ask AI"/>
            <TopIcon icon={Bell} testid="command-notifications" title="Notifications"
                     badge={counts.total}/>
            <button
              onClick={() => navigate("/app/settings")}
              data-testid="command-avatar"
              className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center transition-transform active:scale-95"
              style={{ border: "1.5px solid #D4AF37", background: "rgba(0,0,0,0.6)",
                       boxShadow: "0 0 12px rgba(255,215,0,0.35)" }}
              aria-label="Profile">
              {user?.avatar_image ? (
                <img src={`${API}/files/${user.avatar_image}`} alt="" className="w-full h-full object-cover"/>
              ) : (
                <span className="text-[13px] font-display text-[#FFD700]">
                  {(user?.name || user?.username || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Route body */}
        <main className="min-h-[calc(100vh-64px)] px-4 sm:px-6 lg:px-8 py-5 lg:py-7"
              data-testid="command-main">
          {children}
        </main>
      </div>

      {/* Search palette (⌘K style) */}
      <AnimatePresence>
        {searchOpen && (
          <SearchPalette onClose={() => setSearchOpen(false)}/>
        )}
      </AnimatePresence>

      {/* Global quick-create Triforce FAB */}
      <TriforceCreate/>
    </div>
  );
}


function TopIcon({ icon: Icon, testid, title, badge = 0 }) {
  return (
    <button
      type="button"
      data-testid={testid}
      title={title}
      className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-transform active:scale-95"
      style={{ border: "1px solid rgba(212,175,55,0.45)", background: "rgba(0,0,0,0.45)" }}>
      <Icon size={16} weight="fill" className="text-[#FFD700]"/>
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center text-[9px] font-display tabular-nums"
          style={{
            background: "linear-gradient(180deg, #FF7A8F, #FF5D73 60%, #a01a34)",
            color: "#fff",
            boxShadow: "0 0 10px rgba(255,93,115,0.55)",
            border: "1px solid rgba(0,0,0,0.6)",
          }}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}


function SidebarInner({ user, pathname, onLogout, onClose, counts = {} }) {
  const navigate = useNavigate();
  // Map badge counts to nav keys. Extend here when future modules need
  // notification badges (e.g. tasks: counts.tasks_overdue).
  const BADGES = {
    messages: (counts.messages_unread || 0) + (counts.friend_requests || 0),
  };
  return (
    <>
      {/* Logo header */}
      <div className="px-5 pt-5 pb-4 flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-display text-[22px] leading-none"
               style={{
                 background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 50%, #8a6a00 100%)",
                 WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                 letterSpacing: "0.06em",
                 textShadow: "0 0 24px rgba(255,215,0,0.35)",
               }}>
            HigherVision
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-[#F2DC8C]/60 mt-1">
            Command Center
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} data-testid="command-drawer-close"
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}
                  aria-label="Close menu">
            <X size={14} className="text-[#FFD700]"/>
          </button>
        )}
      </div>

      <div className="mx-4 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.55), transparent)" }}/>

      {/* Live Hawaii time */}
      <div className="px-5 pt-3 pb-1" data-testid="sidebar-hst-clock">
        <HSTClock variant="sidebar"/>
      </div>

      <div className="mx-4 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.35), transparent)" }}/>

      {/* Nav list */}
      <nav className="mt-3 flex-1 overflow-y-auto px-3 space-y-0.5" data-testid="command-nav">
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = item.to === "/app"
            ? pathname === "/app" || pathname === "/app/"
            : pathname.startsWith(item.to);
          return (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className="group flex items-center gap-3 px-3 h-10 rounded-xl font-display text-[13px] tracking-wide transition-all"
              style={{
                background: active
                  ? "linear-gradient(90deg, rgba(255,215,0,0.16) 0%, rgba(212,175,55,0.05) 100%)"
                  : "transparent",
                border: active ? "1px solid rgba(255,215,0,0.55)" : "1px solid transparent",
                color: active ? "#FFD700" : "#F2DC8C",
                boxShadow: active ? "0 0 18px rgba(255,215,0,0.18)" : undefined,
              }}>
              <Icon size={16} weight={active ? "fill" : "regular"}
                    style={{ color: active ? "#FFD700" : "#D4AF37" }}/>
              <span className="truncate">{item.label}</span>
              {BADGES[item.key] > 0 && (
                <span
                  data-testid={`nav-badge-${item.key}`}
                  className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-display tabular-nums"
                  style={{
                    background: "linear-gradient(180deg, #FF7A8F, #FF5D73 60%, #a01a34)",
                    color: "#fff",
                    boxShadow: "0 0 10px rgba(255,93,115,0.55)",
                  }}>
                  {BADGES[item.key] > 99 ? "99+" : BADGES[item.key]}
                </span>
              )}
              {active && !BADGES[item.key] && (
                <span className="ml-auto w-1.5 h-1.5 rotate-45 bg-[#FFD700]"
                      style={{ boxShadow: "0 0 8px #FFD700" }}/>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User footer / upgrade */}
      <div className="p-4 space-y-3 border-t" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
        <button
          type="button"
          onClick={() => navigate("/app/settings")}
          data-testid="command-user-card"
          className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors hover:bg-[rgba(255,215,0,0.06)]"
          style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.4)" }}>
          <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center shrink-0"
               style={{ border: "1.5px solid #D4AF37" }}>
            {user?.avatar_image ? (
              <img src={`${API}/files/${user.avatar_image}`} alt="" className="w-full h-full object-cover"/>
            ) : (
              <span className="text-[13px] font-display text-[#FFD700]">
                {(user?.name || user?.username || "?").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-display text-[#FFD700] truncate">
              {user?.name || user?.username || "Guest"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 truncate">
              {user?.role === "admin" ? "Admin" : (user?.membership_tier || "Free")} tier
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => navigate("/app/settings")}
          data-testid="command-upgrade"
          className="w-full h-10 rounded-xl flex items-center justify-center gap-2 font-display uppercase text-[11px] tracking-[0.22em] transition-transform active:scale-95"
          style={{
            background: "linear-gradient(180deg, #FFF3AE 0%, #D4AF37 60%, #6b4d00 100%)",
            color: "#0a0703",
            boxShadow: "0 6px 20px rgba(255,215,0,0.35)",
          }}>
          <ArrowUp size={12} weight="bold"/>
          Upgrade
        </button>

        {user && (
          <button
            type="button"
            onClick={() => { onLogout?.(); }}
            data-testid="command-logout"
            className="w-full h-9 rounded-xl flex items-center justify-center gap-2 font-display uppercase text-[10px] tracking-[0.22em] text-[#F2DC8C]/80 hover:text-[#FFD700] transition-colors">
            <SignOut size={11}/> Log out
          </button>
        )}
      </div>
    </>
  );
}


/**
 * SearchPalette — command-K style overlay that hits `/api/search`
 * with a debounced query and groups results by module. Arrow-key +
 * Enter navigation, Escape to dismiss.
 */
function SearchPalette({ onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);

  // Flatten grouped results into a single navigable list.
  const flat = useMemo(() => {
    if (!data?.results) return [];
    const out = [];
    for (const [group, items] of Object.entries(data.results)) {
      for (const item of items) out.push({ ...item, group });
    }
    return out;
  }, [data]);

  useEffect(() => {
    setCursor(0);
    if (!q.trim()) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        if (!cancelled) setData(data);
      } catch { if (!cancelled) setData(null); }
      finally { if (!cancelled) setLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const go = (item) => {
    if (!item?.url) return;
    onClose();
    navigate(item.url);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (!flat.length) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      if (e.key === "Enter")     { e.preventDefault(); go(flat[cursor]); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [flat, cursor, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
      data-testid="command-palette"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
      onClick={onClose}>
      <motion.div
        initial={{ y: -20, scale: 0.97, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: -20, scale: 0.97, opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
          border: "1px solid rgba(212,175,55,0.65)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.8), 0 0 40px rgba(255,215,0,0.15)",
          maxHeight: "70vh",
        }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b"
             style={{ borderColor: "rgba(212,175,55,0.3)" }}>
          <MagnifyingGlass size={18} weight="bold" className="text-[#FFD700]"/>
          <input
            autoFocus
            data-testid="command-palette-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people, teams, projects, tasks, meetups, events, jobs, businesses…"
            className="flex-1 bg-transparent text-[15px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/45 focus:outline-none"
          />
          <button onClick={onClose} data-testid="command-palette-close"
                  className="text-[10px] uppercase tracking-widest px-2 py-1 rounded"
                  style={{ border: "1px solid rgba(212,175,55,0.5)", color: "#F2DC8C" }}>
            ESC
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {!q.trim() ? (
            <div className="p-6 text-center text-[12px] text-[#F2DC8C]/55">
              Start typing to search across every module of your Command Center.
            </div>
          ) : loading ? (
            <div className="p-6 text-center text-[12px] text-[#F2DC8C]/65">Searching…</div>
          ) : !flat.length ? (
            <div className="p-6 text-center text-[12px] text-[#F2DC8C]/55" data-testid="command-palette-empty">
              No matches for &ldquo;{q}&rdquo;.
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(data.results).map(([group, items]) => (
                items.length > 0 && (
                  <div key={group}>
                    <div className="text-[9px] uppercase tracking-[0.28em] text-[#F2DC8C]/60 px-3 mb-1">
                      {group}
                    </div>
                    {items.map((it) => {
                      const idx = flat.indexOf(flat.find((f) => f.type === it.type && f.id === it.id));
                      const active = idx === cursor;
                      return (
                        <ResultRow
                          key={it.type + it.id}
                          item={it}
                          active={active}
                          onClick={() => go(it)}
                          onHover={() => setCursor(idx)}
                        />
                      );
                    })}
                  </div>
                )
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-4 py-2 border-t text-[9px] uppercase tracking-widest text-[#F2DC8C]/45"
             style={{ borderColor: "rgba(212,175,55,0.3)" }}>
          <span>↑ ↓ navigate · ⏎ open · ESC close</span>
          {data && <span>{data.total} result{data.total === 1 ? "" : "s"}</span>}
        </div>
      </motion.div>
    </motion.div>
  );
}


// Icon lookup for each search-result category.
const RESULT_ICONS = {
  person:    User,
  team:      UsersFour,
  project:   Folder,
  task:      ListChecks,
  meetup:    UsersThree,
  event:     CalendarBlank,
  job:       Briefcase,
  business:  Storefront,
  message:   ChatCircle,
};


function ResultRow({ item, active, onClick, onHover }) {
  const Icon = RESULT_ICONS[item.type] || MagnifyingGlass;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onHover}
      data-testid={`palette-result-${item.type}-${item.id}`}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors"
      style={active
        ? { background: "rgba(255,215,0,0.10)", border: "1px solid rgba(255,215,0,0.55)" }
        : { border: "1px solid transparent" }}>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
        <Icon size={13} weight="fill" color="#0a0703"/>
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[#F2DC8C] truncate">{item.title}</div>
        {item.subtitle && (
          <div className="text-[10px] text-[#F2DC8C]/60 truncate">{item.subtitle}</div>
        )}
      </div>
      <span className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/50 shrink-0">
        {item.type}
      </span>
    </button>
  );
}
